import { useEffect, useState } from "react";
import { MessageCircle, Sparkles, ArrowRight, Check } from "lucide-react";

const COLORS = ["#FF6B6B", "#FFD93D", "#4FACFE", "#A8E6CF", "#FFB347", "#FF9A9E"];

const Success = () => {
  const [show, setShow] = useState(false);
  const [confetti, setConfetti] = useState([]);

  useEffect(() => {
    setTimeout(() => setShow(true), 80);

    // Generate confetti pieces
    const pieces = Array.from({ length: 14 }, (_, i) => ({
      id: i,
      color: COLORS[i % COLORS.length],
      left: `${Math.random() * 90 + 5}%`,
      delay: `${Math.random() * 0.8}s`,
      size: Math.random() * 8 + 6,
      shape: i % 2 === 0 ? "rounded-full" : "rounded-sm",
    }));
    setConfetti(pieces);
  }, []);

  return (
    <div className="min-h-screen bg-[#FFFDF9] flex items-center justify-center px-4 py-10 overflow-hidden relative">
      {/* Confetti */}
      {confetti.map((piece) => (
        <div
          key={piece.id}
          className={`absolute top-0 ${piece.shape} confetti-piece pointer-events-none`}
          style={{
            left: piece.left,
            width: piece.size,
            height: piece.size,
            backgroundColor: piece.color,
            animationDelay: piece.delay,
          }}
        />
      ))}

      <div
        className="max-w-md w-full text-center transition-all duration-700"
        style={{
          opacity: show ? 1 : 0,
          transform: show ? "translateY(0)" : "translateY(24px)",
        }}
      >
        {/* Success icon */}
        <div className="relative w-24 h-24 mx-auto mb-6">
          <div className="absolute inset-0 rounded-full bg-[#A8E6CF]/20 animate-ping" />
          <div
            className="w-24 h-24 rounded-full bg-[#A8E6CF]/30 flex items-center justify-center pop-in"
          >
            <Check size={40} className="text-green-600" strokeWidth={2.5} />
          </div>
        </div>

        <h1
          style={{ fontFamily: "'Young Serif', Georgia, serif" }}
          className="text-5xl font-bold text-[#2D3436] mb-3 tracking-tight"
        >
          You're all set!
        </h1>
        <p className="text-[#636E72] text-base leading-relaxed mb-8">
          Your personalized prompts are queued and on their way.
          Check your phone — your first one arrives tomorrow.
        </p>

        {/* Timeline card */}
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgba(0,0,0,0.06)] p-6 border border-[#F1F3F5] text-left mb-6">
          <h3
            style={{ fontFamily: "'Young Serif', Georgia, serif" }}
            className="font-bold text-[#2D3436] text-xl mb-4"
          >
            Your free trial journey
          </h3>
          <div className="space-y-4">
            {[
              {
                day: "Tomorrow",
                Icon: MessageCircle,
                color: "bg-[#FF6B6B]",
                text: "Your first personalized prompt arrives by text",
              },
              {
                day: "Day 3",
                Icon: MessageCircle,
                color: "bg-[#FFD93D]",
                text: "Second prompt — go deeper into your story",
              },
              {
                day: "Day 5",
                Icon: MessageCircle,
                color: "bg-[#4FACFE]",
                text: "Third prompt — a client win or behind the scenes",
              },
              {
                day: "Day 7",
                Icon: MessageCircle,
                color: "bg-[#FFB347]",
                text: "Your 4th and final free prompt",
              },
              {
                day: "Day 9",
                Icon: Sparkles,
                color: "bg-[#A8E6CF]",
                text: "An invitation to continue — starting at $99/mo",
              },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <div
                  className={`w-9 h-9 rounded-full ${item.color} flex items-center justify-center flex-shrink-0`}
                >
                  <item.Icon size={15} className="text-white" />
                </div>
                <div className="pt-0.5">
                  <span
                    className="text-xs font-bold text-[#FF6B6B] block"
                    style={{ fontFamily: "'Bitter', Georgia, serif" }}
                  >
                    {item.day}
                  </span>
                  <p className="text-sm text-[#2D3436]">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reply instruction */}
        <div className="bg-[#FFF5F5] rounded-2xl p-4 border border-[#FFE3E3] mb-6 text-left">
          <p className="text-sm text-[#2D3436] font-semibold mb-1">
            How to respond to your prompts:
          </p>
          <p className="text-sm text-[#636E72]">
            Just reply to the text! A few sentences, a voice memo, or even a photo
            works great. There's no wrong answer.
          </p>
        </div>

        {/* CTA to learn more */}
        <a
          data-testid="success-learn-more-link"
          href="https://www.contentoncue.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-[#FF6B6B] font-semibold text-sm hover:gap-3 transition-all duration-200 mb-4"
        >
          Learn more about Content on Cue <ArrowRight size={14} />
        </a>

        <p className="text-xs text-[#636E72] pb-4">
          To opt out of messages at any time, reply STOP to any text.
        </p>
      </div>
    </div>
  );
};

export default Success;
